<template>
  <div v-if="tickets.length" class="qtc preview-box le-card" :class="'qtc--' + state.tone">
    <div class="text-caption grey--text mb-1 d-flex align-center">
      <v-icon x-small class="mr-1">mdi-headset</v-icon>
      Support-Ticket
      <span class="qtc__chip" :class="'qtc__chip--' + state.tone">{{ state.label }}</span>
      <span v-if="onHold" class="qtc__chip qtc__chip--hold">Hold</span>
      <v-spacer />
      <v-btn x-small icon title="Ticket öffnen und antworten" @click="threadOpen = true"><v-icon x-small>mdi-forum-outline</v-icon></v-btn>
    </div>

    <!-- Ticket-Dialog wie an den Task-/Bestell-Karten (QuelpThread + Versandstatus), ohne Seitenwechsel -->
    <v-dialog v-model="threadOpen" max-width="900" content-class="support-dialog">
      <div v-if="threadOpen && ticketForThread" class="qtc__dialog">
        <div class="qtc__dialogHead">
          <b>Support · {{ orderName || orderUid }}</b>
          <v-spacer />
          <v-btn icon small @click="threadOpen = false"><v-icon small>mdi-close</v-icon></v-btn>
        </div>
        <QuelpThread :ticket="ticketForThread" compact @open-order="threadOpen = false" @sent="threadOpen = false" />
      </div>
    </v-dialog>

    <div v-if="subject" class="qtc__subject">{{ subject }}</div>
    <div v-if="tickets.length > 1" class="qtc__muted">{{ tickets.length }} Tickets zu dieser Bestellung, neueste Nachrichten zuerst</div>

    <!-- Erledigt: eingeklappt, auf Klick die Nachrichten -->
    <button v-if="state.key === 'done' && !expanded" type="button" class="qtc__toggle" @click="expanded = true">Nachrichten anzeigen ({{ messages.length }})</button>

    <template v-else>
      <div v-for="m in shownMessages" :key="m.id" class="qtc__msg" :class="{ 'qtc__msg--ours': m.direction === 'outbound' }">
        <div class="qtc__meta">
          <b>{{ m.direction === "outbound" ? "Team" : "Kunde" }}</b>
          <span>· {{ ago(m.date) }}</span>
          <v-spacer />
          <button v-if="m.textTranslated && m.text" type="button" class="qtc__lang" @click="toggleOriginal(m.id)">{{ original[m.id] ? "Übersetzung" : "Original" }}</button>
        </div>
        <div class="qtc__text" :class="{ 'qtc__text--clamp': !full[m.id] }">{{ textOf(m) }}</div>
        <button v-if="isLong(m)" type="button" class="qtc__more" @click="$set(full, m.id, !full[m.id])">{{ full[m.id] ? "weniger" : "mehr" }}</button>
        <div v-if="images(m).length" class="qtc__atts">
          <a v-for="a in images(m)" :key="a.id || a.url" :href="a.url" target="_blank" rel="noopener" :title="a.filename"><img :src="a.url" :alt="a.filename" /></a>
        </div>
        <div v-if="m.direction !== 'outbound'" class="qtc__actions">
          <v-btn x-small text :title="'Kopieren'" @click="copy(m)"><v-icon x-small left>mdi-content-copy</v-icon>Kopieren</v-btn>
          <v-btn v-if="canAiEdit" x-small text title="Als KI-Prompt übernehmen" @click="$emit('use-prompt', textOf(m))"><v-icon x-small left>mdi-auto-fix</v-icon>Als KI-Prompt</v-btn>
        </div>
      </div>
      <button v-if="messages.length > shownMessages.length" type="button" class="qtc__toggle" @click="showAll = true">Ältere Nachrichten ({{ messages.length - shownMessages.length }})</button>
    </template>
  </div>
</template>

<script>
// Quelp-Ticket im LiveEditor (Malte 16.10.): live aus dem Webhook-Spiegel support/{orderUid}
// (quelpTickets[].messages). Nur lesen — geantwortet wird in der Support-Ansicht.
// Ohne Leserecht auf support/ (Firestore-Regeln: ADMIN / MANAGE_RATINGS) bleibt die Karte einfach leer.
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../../fb";
import { EventBus, events } from "../../../../event-bus";
import { getDateFromNowShort } from "@/utils";
import QuelpThread from "@/components/Support/QuelpThread";

const stripHtml = (html) =>
  String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export default {
  name: "QuelpTicketCard",
  components: { QuelpThread },
  props: {
    orderUid: { type: String, default: "" },
    canAiEdit: { type: Boolean, default: false },
    orderName: { type: String, default: "" },
  },
  data: () => ({ support: null, expanded: false, showAll: false, full: {}, original: {}, threadOpen: false }),
  computed: {
    tickets() {
      // quelpTickets ist eine Map { ticketId: ticket } (wie in QuelpThread), ältere Spiegel ggf. Liste
      const raw = this.support && this.support.quelpTickets;
      const list = (Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.values(raw) : []).filter((t) => t && typeof t === "object");
      return list.slice().sort((a, b) => String(b.lastMessage && b.lastMessage.date || b.updatedAt || "").localeCompare(String(a.lastMessage && a.lastMessage.date || a.updatedAt || "")));
    },
    /** Alle Nachrichten aller Tickets, neueste zuerst */
    messages() {
      return this.tickets
        .flatMap((t) => (Array.isArray(t.messages) ? t.messages : []))
        .filter((m) => m && (m.text || m.textTranslated || (m.attachments && m.attachments.length)))
        .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    },
    shownMessages() {
      return this.showAll ? this.messages : this.messages.slice(0, 3);
    },
    /** Gleiche Form wie die Tickets im Support-Store (Dokument + orderUid) */
    ticketForThread() {
      return this.support ? { ...this.support, orderUid: this.support.orderUid || String(this.orderUid) } : null;
    },
    subject() {
      return (this.tickets[0] && this.tickets[0].subject) || "";
    },
    onHold() {
      return !!(this.support && this.support.quelpOnHold);
    },
    /** Status wie in der Support-Ansicht: pending && !waiting = Kunde wartet, waiting = beantwortet, sonst erledigt */
    state() {
      const s = this.support || {};
      if (s.pending && !s.waiting) return { key: "open", label: "Kunde wartet", tone: "open" };
      if (s.pending) return { key: "answered", label: "Beantwortet", tone: "answered" };
      return { key: "done", label: "Erledigt", tone: "done" };
    },
  },
  watch: {
    orderUid: { immediate: true, handler: "subscribe" },
    tickets(v) {
      this.$emit("status", v.length ? { ...this.state, onHold: this.onHold } : null);
    },
    state() {
      if (this.tickets.length) this.$emit("status", { ...this.state, onHold: this.onHold });
    },
  },
  beforeDestroy() {
    if (this.unsub) this.unsub();
  },
  methods: {
    subscribe() {
      if (this.unsub) this.unsub();
      this.unsub = null;
      this.support = null;
      if (!this.orderUid) return;
      this.unsub = onSnapshot(
        doc(db, "support", String(this.orderUid)),
        (snap) => (this.support = snap.exists() ? snap.data() : null),
        () => (this.support = null) // keine Leserechte → Karte bleibt aus
      );
    },
    textOf(m) {
      const t = this.original[m.id] ? m.text : m.textTranslated || m.text;
      return stripHtml(t);
    },
    isLong(m) {
      return this.textOf(m).length > 240;
    },
    toggleOriginal(id) {
      this.$set(this.original, id, !this.original[id]);
    },
    images(m) {
      return (m.attachments || []).filter((a) => a && a.url && String(a.mimeType || "").startsWith("image/"));
    },
    ago(d) {
      return d ? getDateFromNowShort(d) : "";
    },
    copy(m) {
      if (navigator.clipboard) navigator.clipboard.writeText(this.textOf(m));
      EventBus.$emit(events.SNACKBAR, { message: "Nachricht kopiert", type: "success" });
    },

  },
};
</script>

<style lang="scss" scoped>
.qtc__dialog { background: #fff; border-radius: 16px; overflow: hidden; display: flex; flex-direction: column; max-height: 88vh; }
.qtc__dialogHead { display: flex; align-items: center; padding: 10px 14px; border-bottom: 1px solid rgba(0, 0, 0, 0.06); }
/* Kopfzeile wie die übrigen LiveEditor-Karten (deren scoped CSS erreicht nur das Wurzelelement dieser Komponente) */
.qtc .text-caption {
  display: flex; align-items: center; min-height: 24px; font-size: 12.5px !important; font-weight: 600; line-height: 1.2;
  letter-spacing: 0; text-transform: none; color: rgba(0, 0, 0, 0.82) !important; margin-bottom: 8px !important;
  > .v-icon:first-child { width: 24px; height: 24px; margin-right: 8px !important; border-radius: 8px; background: #fde8e6; color: #b23a31 !important; font-size: 14px !important; }
  .v-btn { margin-left: 2px; color: rgba(0, 0, 0, 0.45); }
}
.qtc--answered .text-caption > .v-icon:first-child { background: #e3eefb; color: #1c5cab !important; }
.qtc--done .text-caption > .v-icon:first-child { background: #eef0f3; color: rgba(0, 0, 0, 0.6) !important; }
.qtc {
  &__chip { margin-left: 8px; font-size: 10.5px; font-weight: 700; border-radius: 999px; padding: 1px 8px; background: #eef0f3; color: rgba(0, 0, 0, 0.6);
    &--open { background: rgba(216, 73, 63, 0.12); color: #b23a31; }
    &--answered { background: rgba(42, 120, 214, 0.12); color: #1c5cab; }
    &--done { background: #eef0f3; color: rgba(0, 0, 0, 0.5); }
    &--hold { background: rgba(240, 169, 46, 0.16); color: #a86f00; margin-left: 4px; }
  }
  &--open { box-shadow: inset 3px 0 0 #d8493f, 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07) !important; }
  &--done { opacity: 0.85; }
  &__subject { font-size: 12.5px; font-weight: 600; margin-bottom: 6px; }
  &__muted { font-size: 11px; color: rgba(0, 0, 0, 0.5); margin-bottom: 6px; }
  &__msg { border-radius: 10px; background: #fff8ec; padding: 8px 10px; margin-bottom: 6px;
    &--ours { background: #f3f4f6; }
  }
  &__meta { display: flex; align-items: center; gap: 4px; font-size: 11px; color: rgba(0, 0, 0, 0.55); margin-bottom: 3px; b { color: rgba(0, 0, 0, 0.8); } }
  &__text { font-size: 12.5px; white-space: pre-wrap; word-break: break-word;
    &--clamp { display: -webkit-box; -webkit-line-clamp: 5; -webkit-box-orient: vertical; overflow: hidden; }
  }
  &__lang, &__more, &__toggle { background: none; border: 0; padding: 0; font-size: 11px; color: #6a4fc4; cursor: pointer; }
  &__toggle { margin: 2px 0 4px; font-weight: 600; }
  &__atts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px;
    img { width: 64px; height: 64px; object-fit: cover; border-radius: 8px; display: block; }
  }
  &__actions { display: flex; gap: 2px; margin-top: 4px; .v-btn { text-transform: none; letter-spacing: 0; color: rgba(0, 0, 0, 0.55); } }
}
</style>
